import { Button, List, Form, Row, Space, Typography, Col } from 'antd';
import React, { useMemo } from 'react';
import { renderInput } from '../renderInput';
import {
  CopyOutlined,
  DeleteOutlined,
  HolderOutlined
} from '@ant-design/icons';
import classes from './ListItem.module.css';
import Modal from 'app/components/Modal';
import type { Identifier, XYCoord } from 'dnd-core';
import { useDrop, useDrag } from 'react-dnd';
import { sortBy } from 'lodash';

interface DragItem {
  index: number;
  id: string;
  type: string;
  onDrag?: () => void;
}

export const CheckListItem = ({
  fields,
  config,
  name,
  formInstance,
  namePath,
  onCopy,
  onDelete,
  onMove,
  index,
  readonly = false
}) => {
  const ref = React.useRef<any>(null);
  const dropRef = React.useRef(null);
  const previewRef = React.useRef(null);
  const [, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: 'box',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      onMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    }
  });
  const [{ ...collected }, drag, dragPreview] = useDrag({
    type: 'box',
    item: () => {
      return { id: index, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: () => !readonly
  });
  // const opacity = collected.isDragging ? 0 : 1;

  const showWarningPopup = (e: any) => {
    e.preventDefault();
    Modal.danger({
      type: 'confirm',
      title: 'Are sure you want to delete ?',
      content: 'Warning! Current data has not been saved yet.',
      onOk: () => {
        onDelete(index);
      }
    });
  };
  const additionalInputProps =
    (fieldName: string, propertyName: any) => (config: any) => {
      if (fieldName === 'defectClassification') {
        return {
          ...config.inputProps,
          onChange: (value: any) => {
            const transformValue = config.options.filter((option: any) => {
              return value.includes(option.value);
            });
            formInstance.form.setFieldValue(
              [...formInstance.rootName, ...propertyName, config.name],
              transformValue
            );
          }
        };
      }
      return {};
    };

  drop(dropRef);

  const grouped = useMemo(() => {
    return fields.reduce((acc, field) => {
      if (['checkPointName'].includes(field.name)) {
        const group = acc[0] || [];
        group.unshift(field);
        acc[0] = sortBy(group, 'order');
      }
      if (['checkCriteria'].includes(field.name)) {
        const group = acc[1] || [];
        group.unshift(field);
        acc[1] = sortBy(group, 'order');
      }
      if (['result', 'remark', 'referenceMedia'].includes(field.name)) {
        const group = acc[2] || [];
        group.unshift(field);
        acc[2] = sortBy(group, 'order');
      }
      if (['referenceFile'].includes(field.name)) {
        const group = acc[3] || [];
        group.unshift(field);
        acc[3] = sortBy(group, 'order');
      }
      return acc;
    }, []);
  }, [fields]);

  return (
    <List.Item
      ref={dropRef}
      className={classes.checkList}
      colStyle={{
        alignItems: 'start'
      }}
    >
      <Space>
        <div
          hidden={readonly}
          style={{ cursor: 'move' }}
          ref={ref}
          {...collected}
          onMouseOver={() => {
            drag(ref);
            dragPreview(previewRef);
          }}
        >
          <HolderOutlined />
        </div>
        <Space direction="vertical">
          <Button
            hidden={readonly}
            type="text"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.preventDefault();
              onCopy([...fields]);
            }}
          ></Button>
          <Button
            hidden={readonly}
            type="text"
            icon={<DeleteOutlined />}
            danger
            onClick={showWarningPopup}
          ></Button>
        </Space>
      </Space>
      <Row
        ref={previewRef}
        onMouseOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className={classes.checkListControls}
        gutter={[8, 8]}
      >
        <Col>
          <Typography.Paragraph>#{index + 1}</Typography.Paragraph>
        </Col>

        <Col flex="auto">
          {grouped.map((group, groupIdx) => {
            return (
              <Row key={groupIdx} gutter={[8, 8]}>
                {group.map((field, index) => {
                  const fieldConfig = config.find(
                    ({ fieldName }: any) => fieldName === field.name
                  );
                  const fieldIndex = fields.findIndex(
                    (f) => f.name === field.name
                  );
                  return (
                    <Col span={'auto'} flex="auto" key={index}>
                      <Form.Item
                        style={{ marginBottom: 0 }}
                        label={
                          <Space direction="vertical">
                            <Typography.Text type="secondary">
                              {field.label}
                            </Typography.Text>
                          </Space>
                        }
                      >
                        {field.description && (
                          <Form.Item
                            className={classes.checkListCaptionItem}
                            style={{ marginBottom: 0 }}
                          >
                            <Typography.Text
                              className={classes.caption}
                              type="secondary"
                            >
                              {field.description}
                            </Typography.Text>
                          </Form.Item>
                        )}
                        {fieldConfig &&
                          renderInput(
                            {
                              ...fieldConfig,
                              inputProps: {
                                ...fieldConfig.inputProps,
                                ...additionalInputProps(field.name, [
                                  ...namePath
                                ])(fieldConfig)
                              },
                              readonly
                            },
                            [name, fieldIndex],
                            `${field.name}-${fieldConfig?.name}`,
                            { style: { paddingLeft: 0 } }
                          )}
                      </Form.Item>
                    </Col>
                  );
                })}
              </Row>
            );
          })}
        </Col>
      </Row>
    </List.Item>
  );
};
